<template>
  <TransitionRoot appear :show="first_download_modal" as="template">
    <Dialog
      as="div"
      @close="first_download_modal = false"
      class="relative z-50"
    >
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div
          class="flex min-h-full items-center justify-center p-4 text-center"
        >
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel
              class="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white text-left align-middle shadow-xl transition-all"
            >
              <div class="grid grid-cols-2">
                <div class="col-span-2 sm:col-span-1">
                  <div class="py-6 px-6">
                    <h3 class="font-semibold text-2xl mt-6">
                      {{ dataLocale.data.title }}
                    </h3>

                    <div class="mt-8 text-lg">
                      <p class="mt-4">
                        {{ dataLocale.data.p1 }}
                      </p>
                      <div class="prose mt-4" v-html="dataLocale.data.p2"></div>
                      <div class="flex items-center flex-col mt-12 gap-2">
                        <a target="_blank" href="https://app.qrgateway.com/sign-up"
                          class="flex items-center gap-2 h-[50px] px-4 rounded-xl bg-success text-white drop-shadow-md hover:drop-shadow-xl"
                        >
                          {{ $t("sign_trial") }}
                        </a>
                        <span> No credit card required </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="col-span-1 hidden sm:block relative">
                  <img
                    src="/popmedia.jpg"
                    class="h-full object-cover min-w-full"
                    alt="media-popup"
                  />
                  <button
                    @click="first_download_modal = false"
                    class="absolute top-4 right-4 w-12 h-12 bg-neutral-50 opacity-70 hover:opacity-100 rounded-full flex-center"
                  >
                    <XIcon />
                  </button>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>
<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { XIcon } from "vue-tabler-icons";
import {
  TransitionRoot,
  TransitionChild,
  Dialog,
  DialogPanel,
} from "@headlessui/vue";
import { useQrCreateStore } from "~~/stores/qrcreate";
const { first_download_modal } = storeToRefs(useQrCreateStore());

import DataLocale from "~~/config/interface/DataLocale";

const dataLocale = new DataLocale([
  {
    locale: "en",
    title: " Manage Your QR Codes with an Account",
    p1: "Your free QR code download is just the beginning. Elevate your experience by creating an account.",
    p2: `<span>Track valuable data such as:</span>
                        <ul class="ml-2 mt-0">
                          <li>Scan Count</li>
                          <li>Scan Time</li>
                          <li>Location</li>
                          <li>Device Type (Android / iPhone)</li>
                        </ul>`,
  },
  {
    locale: "vi",
    title: "Quản lý Mã QR của bạn với một Tài khoản",
    p1: "Việc tải xuống mã QR miễn phí chỉ là bắt đầu. Nâng cao trải nghiệm của bạn bằng cách tạo một tài khoản.",
    p2: `<span>Theo dõi dữ liệu có giá trị như:</span>
                        <ul class="ml-2 mt-0">
                          <li>Số lần quét</li>
                          <li>Thời gian quét</li>
                          <li>Vị trí</li>
                          <li>Loại thiết bị (Android / iPhone)</li>
                        </ul>`,
  },
  {
    locale: "de",
    title: "Verwalten Sie Ihre QR-Codes mit einem Konto",
    p1: "Ihr kostenloser Download für QR-Codes ist erst der Anfang. Steigern Sie Ihre Erfahrung, indem Sie ein Konto erstellen.",
    p2: `<span>Verfolgen Sie wertvolle Daten wie:</span>
                        <ul class="ml-2 mt-0">
                          <li>Anzahl der Scans</li>
                          <li>Scan-Zeit</li>
                          <li>Ort</li>
                          <li>Gerätetyp (Android / iPhone)</li>
                        </ul>`,
  },
  {
    locale: "fr",
    title: "Gérez vos codes QR avec un compte",
    p1: "Votre téléchargement gratuit de code QR n'est que le début. Améliorez votre expérience en créant un compte.",
    p2: `<span>Suivez des données précieuses telles que :</span>
                        <ul class="ml-2 mt-0">
                          <li>Nombre de scans</li>
                          <li>Temps de scan</li>
                          <li>Emplacement</li>
                          <li>Type de dispositif (Android / iPhone)</li>
                        </ul>`,
  },
  {
    locale: "ja",
    title: "アカウントでQRコードを管理する",
    p1: "無料のQRコードのダウンロードはたったの始まりです。アカウントを作成することで、体験を向上させます。",
    p2: `<span>次のような貴重なデータを追跡します：</span>
                        <ul class="ml-2 mt-0">
                          <li>スキャン回数</li>
                          <li>スキャン時間</li>
                          <li>場所</li>
                          <li>デバイスタイプ（Android / iPhone）</li>
                        </ul>`,
  },
  {
    locale: "it",
    title: "Gestisci i tuoi codici QR con un account",
    p1: "Il tuo download gratuito di codici QR è solo l'inizio. Eleva la tua esperienza creando un account.",
    p2: `<span>Monitora dati preziosi come:</span>
                        <ul class="ml-2 mt-0">
                          <li>Conteggio delle scansioni</li>
                          <li>Tempo di scansione</li>
                          <li>Posizione</li>
                          <li>Tipo di dispositivo (Android / iPhone)</li>
                        </ul>`,
  },
  {
    locale: "nl",
    title: "Beheer uw QR-codes met een account",
    p1: "Uw gratis QR-code download is slechts het begin. Verrijk uw ervaring door een account aan te maken.",
    p2: '<span>Houd waardevolle gegevens bij, zoals:</span><ul class="ml-2 mt-0"><li>Scankeren</li><li>Scantijd</li><li>Locatie</li><li>Apparaattype (Android / iPhone)</li></ul>',
  },
  {
    locale: "nl",
    title: "Beheer uw QR-codes met een account",
    p1: "Uw gratis QR-code download is slechts het begin. Verrijk uw ervaring door een account aan te maken.",
    p2: '<span>Houd waardevolle gegevens bij, zoals:</span><ul class="ml-2 mt-0"><li>Scankeren</li><li>Scantijd</li><li>Locatie</li><li>Apparaattype (Android / iPhone)</li></ul>',
  },
  {
    locale: "pt",
    title: "Gerencie seus Códigos QR com uma Conta",
    p1: "Seu download gratuito de código QR é apenas o começo. Aprimore sua experiência criando uma conta.",
    p2: '<span>Acompanhe dados valiosos, como:</span><ul class="ml-2 mt-0"><li>Número de Escaneamentos</li><li>Tempo de Escaneamento</li><li>Localização</li><li>Tipo de Dispositivo (Android / iPhone)</li></ul>',
  },
  {
    locale: "zh",
    title: "使用帐户管理您的QR码",
    p1: "您的免费QR码下载仅仅是个开始。通过创建帐户来提升您的体验。",
    p2: '<span>跟踪有价值的数据，如：</span><ul class="ml-2 mt-0"><li>扫描次数</li><li>扫描时间</li><li>位置</li><li>设备类型（Android / iPhone）</li></ul>',
  },
]);
</script>
